/**
 * Error Tracking Service
 * Provides a unified interface for error tracking that can work with or without Sentry
 * 
 * To enable Sentry:
 * 1. Install: pnpm add @sentry/react
 * 2. Set VITE_SENTRY_DSN in .env
 * 3. Import and initialize Sentry in your main entry point
 */

export interface ErrorContext {
  userId?: string;
  userEmail?: string;
  page?: string;
  action?: string;
  [key: string]: unknown;
}

export interface ErrorTrackingService {
  captureException(error: Error, context?: ErrorContext): void;
  captureMessage(message: string, level?: 'info' | 'warning' | 'error', context?: ErrorContext): void;
  setUser(userId: string, email?: string): void;
  clearUser(): void;
  addBreadcrumb(message: string, category?: string, level?: 'info' | 'warning' | 'error', data?: Record<string, unknown>): void;
}

class DefaultErrorTrackingService implements ErrorTrackingService {
  private user: { id: string; email?: string } | null = null;
  private breadcrumbs: Array<{
    message: string;
    category?: string;
    level: 'info' | 'warning' | 'error';
    data?: Record<string, unknown>;
    timestamp: number;
  }> = [];

  captureException(error: Error, context?: ErrorContext): void {
    const errorInfo = {
      message: error.message,
      stack: error.stack,
      name: error.name,
      context: {
        ...context,
        user: this.user,
        breadcrumbs: this.breadcrumbs.slice(-10), // Last 10 breadcrumbs
      },
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('[Error Tracking]', errorInfo);
    }

    // Try to use Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;
      Sentry.captureException(error, {
        contexts: {
          custom: context,
        },
        user: this.user ? {
          id: this.user.id,
          email: this.user.email,
        } : undefined,
        breadcrumbs: this.breadcrumbs.map(b => ({
          message: b.message,
          category: b.category,
          level: b.level,
          data: b.data,
          timestamp: b.timestamp,
        })),
      });
      return;
    }

    // Fallback: Send to custom endpoint or log service
    // In production, you might want to send to your own error tracking API
    if (import.meta.env.PROD && import.meta.env.VITE_ERROR_TRACKING_ENDPOINT) {
      this.sendToEndpoint('/api/errors', errorInfo).catch(() => {
        // Silently fail if endpoint is not available
      });
    }
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info', context?: ErrorContext): void {
    const messageInfo = {
      message,
      level,
      context: {
        ...context,
        user: this.user,
      },
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    if (import.meta.env.DEV) {
      const logMethod = level === 'error' ? console.error : level === 'warning' ? console.warn : console.info;
      logMethod('[Error Tracking]', messageInfo);
    }

    // Try to use Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;
      Sentry.captureMessage(message, {
        level: level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'info',
        contexts: {
          custom: context,
        },
        user: this.user ? {
          id: this.user.id,
          email: this.user.email,
        } : undefined,
      });
      return;
    }
  }

  setUser(userId: string, email?: string): void {
    this.user = { id: userId, email };

    // Try to use Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;
      Sentry.setUser({
        id: userId,
        email,
      });
    }
  }

  clearUser(): void {
    this.user = null;

    // Try to use Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;
      Sentry.setUser(null);
    }
  }

  addBreadcrumb(
    message: string,
    category?: string,
    level: 'info' | 'warning' | 'error' = 'info',
    data?: Record<string, unknown>
  ): void {
    const breadcrumb = {
      message,
      category,
      level,
      data,
      timestamp: Date.now(),
    };

    this.breadcrumbs.push(breadcrumb);

    // Keep only last 50 breadcrumbs
    if (this.breadcrumbs.length > 50) {
      this.breadcrumbs = this.breadcrumbs.slice(-50);
    }

    // Try to use Sentry if available
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      const Sentry = (window as any).Sentry;
      Sentry.addBreadcrumb({
        message,
        category,
        level: level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'info',
        data,
      });
    }
  }

  private async sendToEndpoint(endpoint: string, data: unknown): Promise<void> {
    try {
      const trackingEndpoint = import.meta.env.VITE_ERROR_TRACKING_ENDPOINT || endpoint;
      await fetch(trackingEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
        keepalive: true, // Ensure request completes even if page unloads
      });
    } catch (error) {
      // Silently fail - don't break the app if error tracking fails
      if (import.meta.env.DEV) {
        console.warn('[Error Tracking] Failed to send error to endpoint:', error);
      }
    }
  }
}

// Singleton instance
export const errorTracking: ErrorTrackingService = new DefaultErrorTrackingService();

/**
 * Initialize Sentry (optional)
 * Call this in your main entry point if you want to use Sentry
 */
export const initSentry = async (): Promise<void> => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[Error Tracking] Sentry DSN not configured, using default error tracking');
    }
    return;
  }

  try {
    // Dynamic import to avoid bundling Sentry if not used
    // Use a variable for the module path to prevent Vite from analyzing it at build time
    const sentryModulePath = '@sentry/react';
    try {
      // @ts-ignore - Dynamic import that may not exist
      const SentryModule = await import(/* @vite-ignore */ sentryModulePath);
      const Sentry = SentryModule.default || SentryModule;
      
      Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
        replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend(event: { request?: { headers?: Record<string, unknown> } }) {
          // Filter out sensitive data
          if (event.request) {
            // Remove sensitive headers
            if (event.request.headers) {
              delete event.request.headers['Authorization'];
              delete event.request.headers['authorization'];
            }
          }
          return event;
        },
      });

      // Make Sentry available globally for the error tracking service
      (window as any).Sentry = Sentry;
      
      if (import.meta.env.DEV) {
        console.info('[Error Tracking] Sentry initialized successfully');
      }
    } catch (importError) {
      // Package not installed - this is fine, we'll work without Sentry
      if (import.meta.env.DEV) {
        console.info('[Error Tracking] Sentry package not installed, using fallback error tracking');
      }
      return;
    }
  } catch (error) {
    // Silently fail - error tracking works without Sentry
    if (import.meta.env.DEV) {
      console.warn('[Error Tracking] Failed to initialize Sentry:', error);
    }
  }
};

/**
 * Helper function to wrap async functions with error tracking
 */
export function withErrorTracking<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context?: ErrorContext
): T {
  return (async (...args: Parameters<T>) => {
    try {
      errorTracking.addBreadcrumb(
        `Executing ${fn.name || 'anonymous function'}`,
        'function',
        'info',
        { args: args.length }
      );
      return await fn(...args);
    } catch (error) {
      errorTracking.captureException(
        error instanceof Error ? error : new Error(String(error)),
        {
          ...context,
          function: fn.name || 'anonymous',
          args: args.length,
        }
      );
      throw error;
    }
  }) as T;
}

